import { GridColDef, GridColumnGroupingModel } from '@mui/x-data-grid-premium';
import moment from 'moment/moment';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ButtonCellComponent,
  EmployeeCellComponent,
  dateCellValueFormatter,
  getFieldNamesFromColumnConfig,
} from '~/components/SolaForceDataGrid';
import { useAllEmployees } from '~/data/employees';
import { useAllOrgUnits } from '~/data/orgunits';
import { AnniversariesReportData } from '~/data/reports';
import { useReportNavigateToView } from '~/scenes/PortalModules/Analytics/common';
import { convertToDateOrUndefined } from '~/utils/helpers/date';

export type AnniversariesReportDataRow = {
  id: string;
  employeeId: number;
  firstName: string;
  lastName: string;
  unitName: string;
  managerName: string;
  originalStartDate: Date | undefined;
  startDate: Date | undefined;
  daysToNextAnniversary: number;
  yearsInService: number;
  monthsInService: number;
  positionTitle?: string;
  positionSubtitle?: string;
  years?: number;
  months?: number;
  totalYears: number;
  totalMonths: number;
};

type PropsType = {
  data: AnniversariesReportData[];
};

export const useAnniversariesReport = ({ data }: PropsType) => {
  const { t } = useTranslation();

  const [nextDays, setNextDays] = useState(-1);

  const { goToEmployeeBasicDataView } = useReportNavigateToView();

  const { getEmployeeById } = useAllEmployees();
  const { orgUnits } = useAllOrgUnits();

  const rows: AnniversariesReportDataRow[] = useMemo(() => {
    const getManagerName = (managerId: number) => {
      const manager = getEmployeeById(managerId);
      return manager ? `${manager.fEmpFirstName} ${manager.fEmpLastName}` : '';
    };

    const getDaysToNextAnniversary = (dob: string): number => {
      const nextAnniversary = moment(convertToDateOrUndefined(dob)).set(
        'year',
        moment().year(),
      );

      if (nextAnniversary.isBefore(moment())) {
        nextAnniversary.add(1, 'year');
      }

      return nextAnniversary.diff(moment(), 'days');
    };

    const getOrgUnitName = (unitId: number): string => {
      const orgUnit = orgUnits?.find((ou) => ou.fOrgUnitId === unitId);
      return orgUnit ? orgUnit.fOrgUnitName : '';
    };

    return data
      .map((anniversaryDetail) => {
        const employeeDetails = getEmployeeById(anniversaryDetail.fEmpId);
        const row = {
          id: `${anniversaryDetail.fEmpId}`,
          employeeId: anniversaryDetail.fEmpId,
          firstName: employeeDetails?.fEmpFirstName || '',
          lastName: employeeDetails?.fEmpLastName || '',
          unitName: getOrgUnitName(employeeDetails?.fEmpUnit || -1),
          managerName: getManagerName(employeeDetails?.fEmpManagerId || -1),
          originalStartDate: convertToDateOrUndefined(
            anniversaryDetail.fEmpOrigStartDate,
          ),
          startDate: convertToDateOrUndefined(anniversaryDetail.fEmpStartDate),
          daysToNextAnniversary: getDaysToNextAnniversary(
            anniversaryDetail.fEmpStartDate,
          ),
          yearsInService: anniversaryDetail.fEmpYearsInService,
          monthsInService: anniversaryDetail.fEmpMonthsInService,
          totalYears: anniversaryDetail.fEmpTotalYears,
          totalMonths: anniversaryDetail.fEmpTotalMonths,
        };
        if (!anniversaryDetail?.fEmpAddPositionDetails?.length) {
          return row;
        }
        return anniversaryDetail.fEmpAddPositionDetails?.map((position) => ({
          ...row,
          id: `${anniversaryDetail.fEmpId}-${position.fEmpAddPosId}`,
          positionTitle: position.fEmpAddPosTitle,
          positionSubtitle: position.fEmpAddPosSubtitle,
          years: Math.floor(position.fEmpAddPosMonths / 12),
          months: Math.floor(position.fEmpAddPosMonths % 12),
        }));
      })
      .flat()
      .filter(({ daysToNextAnniversary }) => {
        if (nextDays === -1) {
          return true;
        }
        return daysToNextAnniversary <= nextDays;
      });
  }, [data, getEmployeeById, nextDays, orgUnits]);

  const { columnsConfigs, columnGroupingModel } = useMemo(() => {
    const employeeColumnConfigs: GridColDef<AnniversariesReportDataRow>[] = [
      {
        field: 'employeeId',
        headerName: t('laPicture'),
        filterable: false,
        sortable: false,
        disableExport: true,
        renderCell: (params) => {
          return (
            <EmployeeCellComponent
              {...params}
              pictureOnly={true}
              onClick={() => {
                goToEmployeeBasicDataView(params.row.employeeId);
              }}
            />
          );
        },
        minWidth: 30,
      },
      {
        field: 'firstName',
        headerName: t('laFirstName'),
        hideable: false,
        renderCell: (params) => {
          return (
            <ButtonCellComponent
              {...params}
              onClick={() => {
                goToEmployeeBasicDataView(params.row.employeeId);
              }}
            />
          );
        },
        minWidth: 90,
      },
      {
        field: 'lastName',
        headerName: t('laLastName'),
        hideable: false,
        renderCell: (params) => {
          return (
            <ButtonCellComponent
              {...params}
              onClick={() => {
                goToEmployeeBasicDataView(params.row.employeeId);
              }}
            />
          );
        },
        minWidth: 110,
      },
      {
        field: 'unitName',
        headerName: t('laOrgUnit'),
        minWidth: 180,
      },
      {
        field: 'managerName',
        headerName: t('laManager'),
        minWidth: 180,
      },
    ];

    const anniversaryColumnConfigs: GridColDef<AnniversariesReportDataRow>[] = [
      {
        field: 'originalStartDate',
        headerName: t('laEmpOrigStartDate'),
        minWidth: 150,
        type: 'date',
        valueFormatter: dateCellValueFormatter,
      },
      {
        field: 'startDate',
        headerName: t('laStartDate'),
        minWidth: 150,
        type: 'date',
        valueFormatter: dateCellValueFormatter,
      },
      {
        field: 'daysToNextAnniversary',
        headerName: t('laDaysToAnni'),
        minWidth: 150,
      },
      {
        field: 'yearsInService',
        headerName: t('laAnni'),
        minWidth: 150,
      },
      {
        field: 'monthsInService',
        headerName: t('laMonthsDev'),
        minWidth: 150,
      },
      {
        field: 'positionTitle',
        headerName: t('laPosition'),
        minWidth: 180,
      },
      {
        field: 'positionSubtitle',
        headerName: t('laPositionSubtitle'),
        minWidth: 180,
      },
      {
        field: 'years',
        headerName: t('laYearsDev'),
        minWidth: 150,
      },
      {
        field: 'months',
        headerName: t('laMonthsDev'),
        minWidth: 150,
      },
      {
        field: 'totalYears',
        headerName: t('laTotalYears'),
        minWidth: 150,
      },
      {
        field: 'totalMonths',
        headerName: t('laTotalMonths'),
        minWidth: 150,
      },
    ];

    const columnGroupingModel: GridColumnGroupingModel = [
      {
        groupId: 'employee',
        headerName: t('laEmployee'),
        children: getFieldNamesFromColumnConfig(employeeColumnConfigs),
      },
      {
        groupId: 'anniversary',
        headerName: t('laWorkAnni'),
        children: getFieldNamesFromColumnConfig(anniversaryColumnConfigs),
      },
    ];

    return {
      columnsConfigs: [...employeeColumnConfigs, ...anniversaryColumnConfigs],
      columnGroupingModel,
    };
  }, [t, goToEmployeeBasicDataView]);

  return {
    table: {
      rows,
      columnsConfigs,
      columnGroupingModel,
    },
    nextDays,
    onNextDaysFilterChange: (value: number) => {
      setNextDays(value);
    },
  };
};
